/**
 * Gestion d'un défi par l'admin : public / privé, suppression.
 *
 * - Privé (`challenge_posts.is_public = false`, défaut depuis 0726) : le défi n'est
 *   pas dans le bloc des défis de la landing ; sa page `/challenge/{slug}` s'ouvre
 *   par son lien, sans être référencée.
 * - Demande de l'auteur (`public_request = 'pending'`, 0726) : l'admin approuve
 *   (public + `approved`) ou refuse (`rejected`).
 * - Supprimer : le média (photo, vidéo, vignette) est retiré du bucket
 *   `challenges` (policy admin, migration 0698), puis la ligne (RLS `admin_all` ;
 *   les réponses partent en cascade, une vidéo déjà diffusée reste).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { ytDb } from '@/lib/admin/youtube-queries';
import { setChallengeBackground } from '@/lib/challenges/challenge-background';

const PUBLIC_PREFIX = '/storage/v1/object/public/challenges/';

/** Chemins Storage (bucket `challenges`) des médias d'un défi, d'après leurs URL publiques. */
export function challengeStoragePaths(...urls: (string | null | undefined)[]): string[] {
  const paths = new Set<string>();
  for (const url of urls) {
    if (!url) continue;
    let pathname: string;
    try {
      pathname = new URL(url).pathname;
    } catch {
      continue;
    }
    const at = pathname.indexOf(PUBLIC_PREFIX);
    if (at < 0) continue;
    const path = decodeURIComponent(pathname.slice(at + PUBLIC_PREFIX.length));
    // un chemin d'élève : {uuid}/{nom} — jamais un dossier entier
    if (/^[0-9a-f-]{36}\/[^/]+$/i.test(path)) paths.add(path);
  }
  return [...paths];
}

function invalidateChallenges(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ['admin-challenges'] });
  qc.invalidateQueries({ queryKey: ['admin-challenge', id] });
}

/** Rend un défi public (visible sur la landing et par son lien) ou privé. */
export function useSetChallengePublic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isPublic }: { id: string; isPublic: boolean }) => {
      const { error } = await ytDb
        .from('challenge_posts')
        // un défi rendu public par l'admin est validé ; repassé privé, la demande retombe
        .update({ is_public: isPublic, public_request: isPublic ? 'approved' : 'none' })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, v) => invalidateChallenges(qc, v.id),
  });
}

/** Modère la demande de l'auteur : approuver (page d'accueil) ou refuser. */
export function useModerateChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) => {
      const { error } = await ytDb
        .from('challenge_posts')
        .update({ is_public: approve, public_request: approve ? 'approved' : 'rejected' })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, v) => invalidateChallenges(qc, v.id),
  });
}

/** Choisit la SKILLCARD de fond d'un défi sans média (`null` = automatique). */
export function useSetChallengeBackground() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cardId }: { id: string; cardId: string | null }) =>
      setChallengeBackground({ id }, cardId),
    onSettled: (_d, _e, v) => invalidateChallenges(qc, v.id),
  });
}

/** Supprime un défi : ses fichiers d'abord, puis la ligne. */
export function useDeleteChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      mediaUrl,
      thumbnailUrl,
    }: {
      id: string;
      mediaUrl: string | null;
      thumbnailUrl: string | null;
    }) => {
      const paths = challengeStoragePaths(mediaUrl, thumbnailUrl);
      if (paths.length) {
        const { error } = await supabase.storage.from('challenges').remove(paths);
        if (error) throw new Error(`Le média n’a pas pu être supprimé : ${error.message}`);
      }
      const { error } = await ytDb.from('challenge_posts').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, v) => invalidateChallenges(qc, v.id),
  });
}
